import { useMemo } from 'react'
import {
    mdiAlert, mdiCalculator, mdiCalendarAlert, mdiCalendarClock, mdiCashMultiple,
    mdiCompare, mdiContentCopy, mdiDatabase, mdiDoorOpen, mdiEmailAlertOutline,
    mdiEyeOffOutline, mdiFileAlertOutline, mdiFileClockOutline, mdiFileQuestionOutline,
    mdiFileRemoveOutline, mdiFileSign, mdiGavel, mdiFormatListChecks, mdiHelpCircleOutline,
    mdiHistory, mdiMessageOutline, mdiMessageQuestionOutline, mdiReceipt, mdiRuler,
    mdiScaleBalance, mdiShareVariantOutline, mdiShieldOffOutline, mdiShieldOutline,
    mdiTrendingUp, mdiAccountGroup, mdiAccountRemoveOutline, mdiWeatherNight, mdiWrench, mdiGauge,
    mdiBullhornOutline, mdiClipboardSearchOutline, mdiHammerWrench,
} from '@mdi/js'
import { $api } from '../api/queryClient'
import type { TicketSubtype } from '../api/schema'

/**
 * Icon name (catalogue) → an MDI path from `@mdi/js` (spec 2026-09-17-07).
 *
 * The catalogue keeps kebab-case names so the seed JSON stays
 * framework-agnostic — they are English identifiers and part of the
 * provisioning contract, so they did NOT change when the app left lucide. This
 * table is the one place that knows which glyph each name draws.
 *
 * Every `icon` in `res-provisioning/types/14-ticket-subtypes.json` must have a
 * key here: `useTicketSubtypes.test.ts` walks that file and fails otherwise,
 * so a subtype never silently falls back to the wrench.
 */
const ICONS: Record<string, string> = {
    gauge: mdiGauge,
    shield: mdiShieldOutline,
    'shield-off': mdiShieldOffOutline,
    'file-warning': mdiFileAlertOutline,
    'file-x': mdiFileRemoveOutline,
    gavel: mdiGavel,
    'file-clock': mdiFileClockOutline,
    'file-question': mdiFileQuestionOutline,
    copy: mdiContentCopy,
    wrench: mdiWrench,
    scale: mdiScaleBalance,
    'scale-balanced': mdiScaleBalance,
    'eye-off': mdiEyeOffOutline,
    'trending-up': mdiTrendingUp,
    'git-compare': mdiCompare,
    'help-circle': mdiHelpCircleOutline,
    'alert-triangle': mdiAlert,
    database: mdiDatabase,
    'share-2': mdiShareVariantOutline,
    moon: mdiWeatherNight,
    coins: mdiCashMultiple,
    receipt: mdiReceipt,
    'calendar-clock': mdiCalendarClock,
    'calendar-alert': mdiCalendarAlert,
    'door-open': mdiDoorOpen,
    calculator: mdiCalculator,
    'file-signature': mdiFileSign,
    history: mdiHistory,
    'list-checks': mdiFormatListChecks,
    users: mdiAccountGroup,
    'mail-question': mdiEmailAlertOutline,
    'user-x': mdiAccountRemoveOutline,
    'message-square': mdiMessageOutline,
    'message-circle-question': mdiMessageQuestionOutline,
    ruler: mdiRuler,
    // Remediation steps and the notice to the occupants (spec 2026-10-27-19).
    'clipboard-search': mdiClipboardSearchOutline,
    'hammer-wrench': mdiHammerWrench,
    bullhorn: mdiBullhornOutline,
}

/**
 * Accent name (catalogue) → a THEME TOKEN class (spec 2026-09-17-06).
 *
 * The catalogue names a colour the way a designer would ("rose", "cyan"), and
 * this map is where that becomes the app's own vocabulary. It used to be raw
 * Tailwind palette pairs (`text-rose-600 dark:text-rose-400`), which bypassed
 * the tokens entirely: a theme change missed them and dark mode was whatever
 * the palette happened to do. Ten palette names collapse onto the six semantic
 * tokens the app actually has — several share one, on purpose, because the
 * catalogue's distinctions are decorative and the tokens' are not.
 *
 * Each class is a full literal string: Tailwind's scanner cannot see
 * `text-${x}`.
 */
const ACCENTS: Record<string, string> = {
    emerald: 'text-success',
    green: 'text-success',
    amber: 'text-warning',
    orange: 'text-warning',
    sky: 'text-info',
    cyan: 'text-info',
    violet: 'text-primary',
    indigo: 'text-primary',
    rose: 'text-destructive',
    red: 'text-destructive',
    slate: 'text-muted-foreground',
}

/** MDI path drawn for a subtype whose `icon` this table does not know. */
export const DEFAULT_ICON = mdiWrench
export const DEFAULT_ACCENT = 'text-muted-foreground'

/** Every catalogue icon name this app can draw. */
export const ICON_NAMES = Object.keys(ICONS)

export function iconFor(name?: string): string {
    return (name && ICONS[name]) || DEFAULT_ICON
}
export function accentFor(name?: string): string {
    return (name && ACCENTS[name]) || DEFAULT_ACCENT
}

/** The seven diagnostic domains, in display order (contract: spec 2026-07-31-01),
 * plus `request` — the human-originated family a resident's demande produces
 * (spec 2026-09-02-05). It sits last because it is not a detector's output. */
export const TICKET_DOMAINS = [
    'document', 'data', 'service', 'insurance', 'finance', 'lease', 'contract', 'request',
] as const
export type TicketDomain = (typeof TICKET_DOMAINS)[number]

export type TicketCatalog = {
    list: TicketSubtype[]
    byCode: Record<string, TicketSubtype>
    byDomain: Record<string, TicketSubtype[]>
    isLoading: boolean
}

/** Fetches and indexes the ticket-subtype catalogue for an org. */
export function useTicketSubtypes(org: string): TicketCatalog {
    const { data, isLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/ticket-subtypes',
        { params: { path: { orgCode: org } } },
        { staleTime: 60 * 60 * 1000 }, // catalogue is static; cache an hour
    )

    return useMemo(() => {
        const list = (data?.data ?? []) as TicketSubtype[]
        const byCode: Record<string, TicketSubtype> = {}
        const byDomain: Record<string, TicketSubtype[]> = {}
        for (const s of list) {
            byCode[s.code] = s
            ;(byDomain[s.domain] ??= []).push(s)
        }
        return { list, byCode, byDomain, isLoading }
    }, [data, isLoading])
}

/** Localized label for a subtype entry, with the requested language first. */
export function subtypeLabel(entry: TicketSubtype | undefined, lang: string): string {
    if (!entry?.label) return ''
    return entry.label[lang] || entry.label.fr || entry.label.en || entry.code
}
